import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { AtsTemplatesSettingsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useAtsContext } from 'modules/ats/lib/atsContext'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { EvaluationForms } from './sections/EvaluationForms'
import { MessageTemplates } from './sections/MessageTemplates'
import { OfferLetterTemplates } from './sections/OfferLetterTemplates'
import { QuestionTemplates } from './sections/QuestionTemplates'

const TemplatesPage = () => {
  const { company } = useCurrentSession()
  const [activeTab, setActiveTab] = useState<
    'custom-questions' | 'messages' | 'evaluation-forms' | 'offer-letter' | undefined
  >(undefined)

  const { data, loading } = useQuery(AtsTemplatesSettingsPageDocument, {
    variables: { companyId: company.id },
  })
  const { canManageAtsSettings } = useAtsContext()

  const featureAvailable = (id: string) => {
    return data?.companies.featureAvailabilitiesConnection.nodes.find((node) => node.id === id)
      ?.available
  }

  const showQuestionTemplates = featureAvailable('ats_customized_features')
  const showMessageTemplates = featureAvailable('ats_message_templates')
  const showEvaluationForms = canManageAtsSettings && featureAvailable('ats_evaluation_forms')
  // TO DO: remove the dev feature flag when the feature is ready
  const showOfferLetterTemplates =
    (data?.features.enabledFeatures.nodes.find((node) => node.name === 'ats_offer_letter')
      ?.enabled &&
      data?.features.enabledFeatures.nodes.find(
        (node) => node.name === 'dev_ats_offer_letter_templates'
      )?.enabled) ??
    false

  const tabs = [
    showQuestionTemplates && {
      id: 'custom-questions',
      label: i18n.t('ats.settings.templates.tabs.custom_questions'),
      onClick: () => setActiveTab('custom-questions'),
      index: true,
    },
    showMessageTemplates && {
      id: 'messages',
      label: i18n.t('ats.settings.templates.tabs.messages'),
      onClick: () => setActiveTab('messages'),
    },
    showEvaluationForms && {
      id: 'evaluation-forms',
      label: i18n.t('ats.settings.templates.tabs.evaluation_forms'),
      onClick: () => setActiveTab('evaluation-forms'),
    },
    showOfferLetterTemplates && {
      id: 'offer-letter',
      label: i18n.t('ats.settings.templates.tabs.offer_letter'),
      onClick: () => setActiveTab('offer-letter'),
    },
  ].filter(Boolean)

  const currentTab = activeTab ?? tabs[0]?.id

  return (
    <ResponsivePage.Body
      error={null}
      loading={loading}
      showHeader={false}
      authorized={tabs.length > 0}
      variant="fullscreen"
    >
      <Tabs tabs={tabs} activeTabId={currentTab} secondary />
      <Box gap="s48">
        {currentTab === 'custom-questions' && <QuestionTemplates companyId={company.id} />}
        {currentTab === 'messages' && <MessageTemplates />}
        {currentTab === 'evaluation-forms' && <EvaluationForms />}
        {currentTab === 'offer-letter' && <OfferLetterTemplates />}
      </Box>
    </ResponsivePage.Body>
  )
}

export default TemplatesPage
